"use client";

import { useEffect, useRef } from "react";
import { Alert } from "../../bausteine/alert";

/**
 * Fehlschlag im Untermenü des Konto-Menüs (r98 „Eine Meldung muss ihren Auslöser überleben“, r105).
 *
 * Preline schließt ein Untermenü beim Klick auf einen Eintrag. Schlägt die Handlung danach fehl, wäre
 * die Meldung in der schon geschlossenen Fläche unsichtbar. Darum öffnet `useUntermenueOffenBeiFehler`
 * das Untermenü über die Preline-API (HSDropdown.open) wieder, sobald ein Fehlschlag vorliegt; das
 * Konto-Menü selbst (`--auto-close: inside`) bleibt beim Klick offen. Die Meldung ist die vorhandene
 * Preline-Alert-Fläche (Ton „warnung“, Fehlschlag wiederholbar, nie ein Code).
 */
const MAX_VERSUCHE = 8;
const WARTE_MS = 120;

export function useUntermenueOffenBeiFehler<T extends HTMLElement>(fehler: boolean) {
  const ref = useRef<T>(null);

  useEffect(() => {
    if (!fehler) return;
    let abgebrochen = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    void import("preline/non-auto")
      .then(({ HSDropdown }) => {
        const huelle = ref.current?.closest<HTMLElement>(".hs-dropdown");
        if (!huelle) return;
        let versuch = 0;
        let selbstGeoeffnet = false;
        // Der Klick schließt das Untermenü mit kurzer Animation (danach erst `hidden`); erst dann nimmt
        // `open` es wieder auf. Ist es nach unserem Öffnen offen, ist die Arbeit getan.
        const oeffne = () => {
          if (abgebrochen) return;
          const menue = huelle.querySelector(":scope > .hs-dropdown-menu");
          const verborgen = !menue || menue.classList.contains("hidden");
          if (!verborgen && selbstGeoeffnet) return;
          if (verborgen) {
            HSDropdown.open(huelle);
            selbstGeoeffnet = true;
          }
          versuch += 1;
          if (versuch < MAX_VERSUCHE) timer = setTimeout(oeffne, WARTE_MS);
        };
        oeffne();
      })
      .catch(() => {
        // Preline nicht verfügbar: die Meldung bleibt im DOM, nur das Wiederöffnen entfällt.
      });

    return () => {
      abgebrochen = true;
      if (timer) clearTimeout(timer);
    };
  }, [fehler]);

  return ref;
}

export function UntermenueFehler({ children }: { children: React.ReactNode }) {
  return (
    <div className="p-1">
      {/* Preline: component/alerts/alerts-soft-color-variants (über den Baustein Alert; Fläche des Untermenüs: ui-components/ui-components-sub-menus-selection/ui-components-cat-account-and-workspace-menus/account-menu-with-status-controls) */}
      <Alert ton="warnung">{children}</Alert>
    </div>
  );
}
